import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import { InputActionMeta } from 'react-select';
import Selector from '../../components/shared/Selector/Selector';
import Field from '../../components/shared/Field/Field';
import InfoBox from '../../components/shared/InfoBox/InfoBox';
import {
  ContextProject,
  HistoricalSuggestion,
  serviceEvaluations,
} from '../../services/service-evaluations';
import { EvaluationLimits } from '../../util/evaluationContract';
import { toProjectOptions } from '../app-activity-modal/activityForm';
import { reportUnexpected } from '../page-supplier-evaluations/reportUnexpected';
import styles from './app-evaluation-start.module.scss';

export const NO_PROJECT = '__none__';
export const HISTORICAL_PROJECT = '__historical__';

export type ContextValue = {
  choice: string | null;
  areaId: string | null;
  historicalName: string;
  historicalNumber: string;
};

export const EMPTY_CONTEXT: ContextValue = {
  choice: null,
  areaId: null,
  historicalName: '',
  historicalNumber: '',
};

type Props = {
  projects: ContextProject[];
  value: ContextValue;
  onChange: (next: ContextValue) => void;
  onChoiceChange: (next: ContextValue, cleared: boolean) => void;
};

type Option = { value: string; label: string; __isNew__?: boolean };

const SUGGESTION_DELAY_MS = 300;

const clip = (text: string) => text.slice(0, EvaluationLimits.HistoricalFieldMaxLength);

const suggestionLabel = (suggestion: HistoricalSuggestion) =>
  suggestion.name && suggestion.number
    ? `${suggestion.name} (${suggestion.number})`
    : suggestion.name || suggestion.number || '';

export const projectOf = (projects: ContextProject[], value: ContextValue) =>
  projects.find(project => project._id === value.choice) ?? null;

export const contextFor = (projects: ContextProject[], choice: string): ContextValue => {
  const project = projects.find(item => item._id === choice);
  const withSupplier = project?.areas.filter(area => area.hasSupplier) ?? [];
  return {
    ...EMPTY_CONTEXT,
    choice,
    areaId: withSupplier.length === 1 ? withSupplier[0].areaId : null,
  };
};

const markSupplierPurchases = (projects: ContextProject[]): Option[] =>
  toProjectOptions(projects).map(option => {
    const purchases = (projects.find(project => project._id === option.value)?.areas ?? [])
      .filter(area => area.hasSupplier)
      .map(area => area.name);
    return purchases.length
      ? { ...option, label: `${option.label} · ${purchases.join(', ')}` }
      : option;
  });

const hasDependents = (value: ContextValue) =>
  Boolean(value.areaId || value.historicalName.trim() || value.historicalNumber.trim());

const ContextField = ({ projects, value, onChange, onChoiceChange }: Props) => {
  const [t] = useTranslation('evaluations');
  const [historicalInput, setHistoricalInput] = useState('');
  const [suggestions, setSuggestions] = useState<HistoricalSuggestion[]>([]);
  const isHistorical = value.choice === HISTORICAL_PROJECT;
  const project = projectOf(projects, value);
  const latestQuery = useRef('');
  const pickedSinceTyping = useRef(false);

  const loadSuggestions = useMemo(
    () => (query: string) => {
      latestQuery.current = query;
      serviceEvaluations
        .getHistoricalSuggestions(query)
        .then(found => {
          if (latestQuery.current === query) setSuggestions(found);
        })
        .catch(reportUnexpected);
    },
    [],
  );
  const loadSuggestionsLater = useMemo(
    () => debounce(loadSuggestions, SUGGESTION_DELAY_MS),
    [loadSuggestions],
  );

  useEffect(() => () => loadSuggestionsLater.cancel(), [loadSuggestionsLater]);

  useEffect(() => {
    if (isHistorical) loadSuggestions('');
  }, [isHistorical, loadSuggestions]);

  const choiceOptions: Option[] = [
    ...markSupplierPurchases(projects),
    { value: NO_PROJECT, label: t('quick.noProject', 'Inget projekt') },
    { value: HISTORICAL_PROJECT, label: t('quick.historical', 'Historiskt projekt…') },
  ];
  const areaOptions: Option[] =
    project?.areas.map(area => ({ value: area.areaId, label: area.name })) ?? [];
  const suggestionOptions: Option[] = suggestions.map(suggestion => ({
    value: suggestion.key,
    label: suggestionLabel(suggestion),
  }));
  const chosenArea = project?.areas.find(area => area.areaId === value.areaId);
  const supplierOutside = project
    ? chosenArea
      ? !chosenArea.hasSupplier
      : !project.areas.some(area => area.hasSupplier)
    : false;

  const changeChoice = (option: Option | null) => {
    if (!option || option.value === value.choice) return;
    setHistoricalInput('');
    onChoiceChange(contextFor(projects, option.value), hasDependents(value));
  };

  const pickHistorical = (option: Option | null) => {
    if (!option) {
      onChange({ ...value, historicalName: '' });
      return;
    }
    const suggestion = option.__isNew__
      ? undefined
      : suggestions.find(item => item.key === option.value);
    onChange({
      ...value,
      historicalName: clip(suggestion ? suggestion.name ?? '' : option.value.trim()),
      historicalNumber: clip(suggestion ? suggestion.number ?? '' : value.historicalNumber),
    });
  };

  const historicalValue = value.historicalName
    ? { value: value.historicalName, label: value.historicalName }
    : null;

  const typeHistorical = (text: string, meta: InputActionMeta) => {
    if (meta.action === 'set-value') pickedSinceTyping.current = true;
    if (meta.action === 'input-change') {
      pickedSinceTyping.current = false;
      setHistoricalInput(text);
      setSuggestions([]);
      latestQuery.current = text;
      loadSuggestionsLater(text);
    }
    if (meta.action === 'input-blur' && meta.prevInputValue.trim() && !pickedSinceTyping.current) {
      setHistoricalInput('');
      pickHistorical({ value: meta.prevInputValue, label: meta.prevInputValue, __isNew__: true });
    }
  };

  return (
    <div className={styles.fields}>
      <Selector<Option>
        isSearchable
        label={t('quick.project', 'Projekt')}
        placeholder={t('quick.projectPlaceholder', 'Välj projekt')}
        options={choiceOptions}
        value={choiceOptions.find(option => option.value === value.choice) ?? null}
        onChange={changeChoice}
      />
      {project && areaOptions.length > 0 && (
        <Selector<Option>
          isClearable
          label={t('quick.purchase', 'Inköp')}
          placeholder={t('quick.purchasePlaceholder', 'Välj inköp (valfritt)')}
          options={areaOptions}
          value={areaOptions.find(option => option.value === value.areaId) ?? null}
          onChange={option => onChange({ ...value, areaId: option?.value ?? null })}
        />
      )}
      {supplierOutside && (
        <InfoBox
          description={
            chosenArea
              ? t(
                  'quick.purchaseOutsideChosen',
                  'Leverantören finns inte med i det valda inköpet. Du kan ändå utvärdera den.',
                )
              : t(
                  'quick.purchaseOutsideAny',
                  'Leverantören finns inte med i något av projektets inköp. Du kan ändå utvärdera den.',
                )
          }
        />
      )}
      {isHistorical && (
        <>
          <Selector<Option>
            isCreatable
            isSearchable
            isClearable
            label={t('quick.historicalName', 'Projektnamn')}
            placeholder={t('quick.historicalNamePlaceholder', 'Sök eller skriv ett namn')}
            options={suggestionOptions}
            value={historicalValue}
            onChange={pickHistorical}
            getSearchString={() => historicalInput}
            formatCreateLabel={text =>
              t('quick.historicalNew', 'Nytt projekt: {{name}}', { name: text })
            }
            onInputChange={typeHistorical}
            {...({ createOptionPosition: 'first' } as object)}
          />
          <Field
            label={t('quick.historicalNumber', 'Projektnummer')}
            value={value.historicalNumber}
            onChange={text => onChange({ ...value, historicalNumber: clip(text) })}
          />
        </>
      )}
    </div>
  );
};

export default ContextField;
